import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, KeyRound, LoaderCircle, Power, PowerOff, ShieldCheck, ShieldOff, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import { API_PREFIX } from "../config";
import { useAuth } from "../AuthContext";
import { DATABASES } from "../utils/databases";
import { MANAGED_FIELDS, isManagedField, isSsoAccount } from "../utils/identity";
import IdentityGroups from "./IdentityGroups";
import "./AdminUsers.css";

// The backend answers an edit of an IdP-managed field (or a password reset of
// an SSO account) with 409 Conflict.
const CONFLICT = 409;

/** Lazily loaded identity panel of one user: linked IdP identities and groups. */
function UserIdentityPanel({ entry }) {
  const { t } = useTranslation();
  if (!entry || entry.status === "loading") {
    return <div className="ds-help admin-identity-panel">{t("adminUsers.identityLoading")}</div>;
  }
  if (entry.status === "error") {
    return <div className="ds-alert ds-alert--danger admin-identity-panel" role="alert">{entry.error}</div>;
  }
  const { identities = [], groups = [] } = entry.data || {};
  return (
    <div className="admin-identity-panel">
      <h4 className="admin-identity-heading">{t("adminUsers.identityTitle")}</h4>
      {identities.length === 0 ? (
        <div className="ds-help">{t("adminUsers.noIdentities")}</div>
      ) : (
        <div className="ds-table-wrap">
          <table className="ds-table">
            <thead>
              <tr>
                <th>{t("adminUsers.provider")}</th>
                <th>{t("adminUsers.subject")}</th>
                <th>{t("adminUsers.tenant")}</th>
                <th>{t("adminUsers.emailAtLogin")}</th>
                <th>{t("adminUsers.lastLogin")}</th>
              </tr>
            </thead>
            <tbody>
              {identities.map((i) => (
                <tr key={`${i.provider}:${i.subject}`}>
                  <td>{i.provider}</td>
                  <td className="ds-mono">{i.subject}</td>
                  <td className="ds-mono">{i.tenant_id || ""}</td>
                  <td>{i.email_at_login || ""}</td>
                  <td>{i.last_login_at ? new Date(i.last_login_at).toLocaleString() : t("adminUsers.never")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h4 className="admin-identity-heading">{t("identity.groups")}</h4>
      <IdentityGroups groups={groups} />
    </div>
  );
}

/** Admin user-management view: list accounts, create a new one, toggle
 * role/active status. Accounts are admin-provisioned only — there is no
 * public sign-up, so this is the only way to create additional logins once
 * the bootstrap admin (scripts/create_admin.py) exists.
 *
 * Inline confirmation only guards the two privilege/access-*reducing*
 * directions (deactivate, promote to admin) — the reverse directions
 * (reactivate, demote to user) are the safe direction of each toggle and
 * stay one-click, matching this app's existing inline-confirm convention
 * (see Sidebar.jsx's conversation delete) rather than introducing a modal.
 */
export default function AdminUsers() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [database, setDatabase] = useState("");          // new-account tenant ('' = all)
  const [creating, setCreating] = useState(false);
  const [wsDraft, setWsDraft] = useState({});            // user id → workspace draft
  const [wsPendingId, setWsPendingId] = useState(null);  // user id currently saving workspace
  const [licDraft, setLicDraft] = useState({});          // user id → licences draft (CSV)
  const [licPendingId, setLicPendingId] = useState(null); // user id currently saving licences
  const [pendingId, setPendingId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { id, type: "deactivate" | "make-admin" }
  const [resetPasswordFor, setResetPasswordFor] = useState(null); // user id currently showing the reset-password input
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [expandedId, setExpandedId] = useState(null);     // user id whose identity panel is open
  const [identities, setIdentities] = useState({});        // user id -> { status, data?, error? }

  const loadIdentity = useCallback(async (userId) => {
    setIdentities((m) => ({ ...m, [userId]: { status: "loading" } }));
    try {
      const data = await apiFetchJson(`${API_PREFIX}/auth/users/${encodeURIComponent(userId)}/identity`);
      setIdentities((m) => ({ ...m, [userId]: { status: "ready", data } }));
    } catch (err) {
      setIdentities((m) => ({ ...m, [userId]: { status: "error", error: err.message } }));
    }
  }, []);

  const toggleDetails = (userId) => {
    if (expandedId === userId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(userId);
    if (!identities[userId] || identities[userId].status === "error") loadIdentity(userId);
  };

  // Shows the backend's message; a 409 means the IdP owns that field (or the
  // password), so the user's identity is (re)loaded to lock its controls.
  const reportError = (u, err) => {
    setError(err.message);
    if (err.status === CONFLICT) loadIdentity(u.id);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await apiFetchJson("/api/v1/auth/users"));
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(""), 2500);
    return () => clearTimeout(t);
  }, [success]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      await apiFetchJson("/api/v1/auth/users", {
        method: "POST",
        body: JSON.stringify({ email, password, role, database: database || null }),
      });
      setSuccess(`Created ${email}`);
      setEmail("");
      setPassword("");
      setRole("user");
      setDatabase("");
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const setActive = async (u, isActive) => {
    setConfirmAction(null);
    setPendingId(u.id);
    setError("");
    try {
      await apiFetchJson(`/api/v1/auth/users/${u.id}/active`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: isActive }),
      });
      setSuccess(isActive ? `${u.email} activated` : `${u.email} deactivated`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingId(null);
    }
  };

  const setRoleFor = async (u, nextRole) => {
    setConfirmAction(null);
    setPendingId(u.id);
    setError("");
    try {
      await apiFetchJson(`/api/v1/auth/users/${u.id}/role`, {
        method: "PATCH",
        body: JSON.stringify({ role: nextRole }),
      });
      setSuccess(`${u.email} is now ${nextRole}`);
      await load();
    } catch (err) {
      reportError(u, err);
    } finally {
      setPendingId(null);
    }
  };

  const resetPassword = async (u) => {
    if (resetPasswordValue.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    setPendingId(u.id);
    setError("");
    try {
      await apiFetchJson(`/api/v1/auth/users/${u.id}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ new_password: resetPasswordValue }),
      });
      setSuccess(`Password reset for ${u.email}`);
      setResetPasswordFor(null);
      setResetPasswordValue("");
    } catch (err) {
      reportError(u, err);
    } finally {
      setPendingId(null);
    }
  };

  const saveWorkspace = async (u) => {
    const value = wsDraft[u.id] ?? u.database ?? "";
    setWsPendingId(u.id);
    setError("");
    try {
      await apiFetchJson(`/api/v1/auth/users/${u.id}/database`, {
        method: "PATCH",
        body: JSON.stringify({ database: value || null }),
      });
      setSuccess(`Workspace updated for ${u.email}`);
      setWsDraft((d) => {
        const next = { ...d };
        delete next[u.id];
        return next;
      });
      await load();
    } catch (err) {
      reportError(u, err);
    } finally {
      setWsPendingId(null);
    }
  };

  const saveLicences = async (u) => {
    const value = licDraft[u.id] ?? (u.licences || []).join(", ");
    const keys = value.split(",").map((k) => k.trim()).filter(Boolean);
    setLicPendingId(u.id);
    setError("");
    try {
      await apiFetchJson(`/api/v1/auth/users/${u.id}/licences`, {
        method: "PATCH",
        body: JSON.stringify({ licences: keys }),
      });
      setSuccess(`Licences updated for ${u.email}`);
      setLicDraft((d) => {
        const next = { ...d };
        delete next[u.id];
        return next;
      });
      await load();
    } catch (err) {
      reportError(u, err);
    } finally {
      setLicPendingId(null);
    }
  };

  const initial = (addr) => (addr?.[0] || "?").toUpperCase();

  return (
    <div className="ds-page admin-users">
      <div className="ds-page-header">
        <h2 className="ds-page-title">User management</h2>
        <p className="ds-page-subtitle">
          Accounts are admin-provisioned only — there's no public sign-up.
        </p>
      </div>

      {error && <div className="ds-alert ds-alert--danger">{error}</div>}
      {success && <div className="ds-alert ds-alert--success">{success}</div>}

      <form className="ds-card admin-create-form" onSubmit={handleCreate}>
        <div className="ds-field">
          <label htmlFor="admin-new-email">Email</label>
          <input
            id="admin-new-email"
            className="ds-input admin-create-control"
            type="email"
            value={email}
            required
            placeholder="name@company.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="ds-field">
          <label htmlFor="admin-new-password">Password</label>
          <input
            id="admin-new-password"
            className="ds-input admin-create-control"
            type="password"
            value={password}
            required
            minLength={8}
            placeholder="At least 8 characters"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="ds-field">
          <label htmlFor="admin-new-role">Role</label>
          <select
            id="admin-new-role"
            className="ds-select admin-create-control--narrow"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="ds-field">
          <label htmlFor="admin-new-workspace">Workspace</label>
          <select
            id="admin-new-workspace"
            className="ds-select admin-create-control"
            value={database}
            onChange={(e) => setDatabase(e.target.value)}
          >
            <option value="">All workspaces</option>
            {DATABASES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <button type="submit" className="ds-btn ds-btn--primary" disabled={creating}>
          {creating ? <LoaderCircle size={14} className="ds-spin" /> : <UserPlus size={14} />}
          {creating ? "Creating…" : "Create user"}
        </button>
      </form>

      {loading ? (
        <div className="ds-empty">Loading users…</div>
      ) : users.length === 0 ? (
        <div className="ds-empty">No users yet.</div>
      ) : (
        <div className="admin-user-list">
          {users.map((u) => {
            const isSelf = currentUser?.id === u.id;
            const isPending = pendingId === u.id;
            const confirming = confirmAction?.id === u.id ? confirmAction.type : null;
            const isAdmin = u.role === "admin";
            const sso = isSsoAccount(u);
            const identity = identities[u.id]?.data;
            const managedRole = isManagedField(identity, MANAGED_FIELDS.role);
            const managedDatabase = isManagedField(identity, MANAGED_FIELDS.database);
            const managedLicences = isManagedField(identity, MANAGED_FIELDS.licences);
            const managedNote = t("identity.managedByIdp");
            const expanded = expandedId === u.id;

            return (
              <div className="admin-user-item" key={u.id}>
              <div className="admin-user-row">
                <div className={`admin-avatar${isAdmin ? " admin-avatar-admin" : ""}`}>
                  {initial(u.email)}
                </div>

                <div className="admin-user-info">
                  <span className="admin-user-email" title={u.email}>{u.email}</span>
                  {u.display_name && <span className="ds-meta">{u.display_name}</span>}
                  {isSelf && <span className="ds-badge">you</span>}
                </div>

                <span className={`ds-badge${sso ? " ds-badge--info" : ""}`}>
                  {sso ? t("identity.sourceSso") : t("identity.sourceLocal")}
                </span>

                <span className={`ds-badge${isAdmin ? " ds-badge--info" : ""}`}>
                  {isAdmin && <ShieldCheck size={10} />}
                  {u.role}
                </span>
                <span className={`ds-badge ${u.is_active ? "ds-badge--success" : "ds-badge--danger"}`}>
                  {u.is_active ? "active" : "inactive"}
                </span>

                <div className="admin-user-workspace">
                  <select
                    className="ds-select ds-select--sm"
                    aria-label={`Workspace for ${u.email}`}
                    disabled={managedDatabase}
                    title={managedDatabase ? managedNote : undefined}
                    value={wsDraft[u.id] ?? u.database ?? ""}
                    onChange={(e) => setWsDraft((d) => ({ ...d, [u.id]: e.target.value }))}
                  >
                    <option value="">All workspaces</option>
                    {DATABASES.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {((wsDraft[u.id] ?? u.database ?? "") !== (u.database ?? "")) && (
                    <button
                      type="button"
                      className="ds-btn ds-btn--primary ds-btn--sm"
                      disabled={wsPendingId === u.id}
                      onClick={() => saveWorkspace(u)}
                    >
                      {wsPendingId === u.id ? <LoaderCircle size={12} className="ds-spin" /> : "Save"}
                    </button>
                  )}
                </div>

                <div className="admin-user-workspace">
                  <input
                    className="ds-input ds-input--sm admin-licences-input"
                    aria-label={`Licences for ${u.email}`}
                    placeholder="Licences (comma-separated)"
                    disabled={managedLicences}
                    title={managedLicences ? managedNote : undefined}
                    value={licDraft[u.id] ?? (u.licences || []).join(", ")}
                    onChange={(e) => setLicDraft((d) => ({ ...d, [u.id]: e.target.value }))}
                  />
                  {(licDraft[u.id] ?? (u.licences || []).join(", ")) !== (u.licences || []).join(", ") && (
                    <button
                      type="button"
                      className="ds-btn ds-btn--primary ds-btn--sm"
                      disabled={licPendingId === u.id}
                      onClick={() => saveLicences(u)}
                    >
                      {licPendingId === u.id ? <LoaderCircle size={12} className="ds-spin" /> : "Save"}
                    </button>
                  )}
                </div>

                <div className="admin-row-actions">
                  {resetPasswordFor === u.id ? (
                    <span className="admin-confirm-inline">
                      <input
                        type="password"
                        className="ds-input ds-input--sm admin-reset-password-input"
                        placeholder="New password"
                        minLength={8}
                        autoFocus
                        value={resetPasswordValue}
                        onChange={(e) => setResetPasswordValue(e.target.value)}
                      />
                      <button
                        className="ds-btn ds-btn--primary ds-btn--sm"
                        disabled={isPending}
                        onClick={() => resetPassword(u)}
                      >
                        {isPending ? <LoaderCircle size={12} className="ds-spin" /> : "Confirm"}
                      </button>
                      <button
                        className="ds-btn ds-btn--sm"
                        onClick={() => {
                          setResetPasswordFor(null);
                          setResetPasswordValue("");
                        }}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : confirming === "deactivate" ? (
                    <span className="admin-confirm-inline">
                      <span className="admin-confirm-label">Deactivate?</span>
                      <button className="ds-btn ds-btn--danger ds-btn--sm" onClick={() => setActive(u, false)}>
                        Yes
                      </button>
                      <button className="ds-btn ds-btn--sm" onClick={() => setConfirmAction(null)}>
                        No
                      </button>
                    </span>
                  ) : confirming === "make-admin" ? (
                    <span className="admin-confirm-inline">
                      <span className="admin-confirm-label">Make admin?</span>
                      <button className="ds-btn ds-btn--primary ds-btn--sm" onClick={() => setRoleFor(u, "admin")}>
                        Yes
                      </button>
                      <button className="ds-btn ds-btn--sm" onClick={() => setConfirmAction(null)}>
                        No
                      </button>
                    </span>
                  ) : (
                    <>
                      <button
                        className="ds-btn ds-btn--sm"
                        disabled={isPending || isSelf || managedRole}
                        title={managedRole ? managedNote : isSelf ? "You can't change your own role" : undefined}
                        onClick={() =>
                          isAdmin ? setRoleFor(u, "user") : setConfirmAction({ id: u.id, type: "make-admin" })
                        }
                      >
                        {isAdmin ? <ShieldOff size={12} /> : <ShieldCheck size={12} />}
                        {isAdmin ? "Make user" : "Make admin"}
                      </button>
                      <button
                        className={`ds-btn ds-btn--sm${u.is_active ? " ds-btn--danger" : ""}`}
                        disabled={isPending || isSelf}
                        title={isSelf ? "You can't deactivate your own account" : undefined}
                        onClick={() =>
                          u.is_active ? setConfirmAction({ id: u.id, type: "deactivate" }) : setActive(u, true)
                        }
                      >
                        {isPending ? (
                          <LoaderCircle size={12} className="ds-spin" />
                        ) : u.is_active ? (
                          <PowerOff size={12} />
                        ) : (
                          <Power size={12} />
                        )}
                        {u.is_active ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        className="ds-btn ds-btn--sm"
                        disabled={isPending || sso}
                        title={sso ? t("adminUsers.resetUnavailable") : "Set a new password for this user directly (no email involved)"}
                        onClick={() => {
                          setResetPasswordFor(u.id);
                          setResetPasswordValue("");
                        }}
                      >
                        <KeyRound size={12} />
                        Reset Password
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="ds-btn ds-btn--ghost ds-btn--sm"
                    aria-expanded={expanded}
                    aria-label={`${expanded ? t("adminUsers.hideDetails") : t("adminUsers.showDetails")} ${u.email}`}
                    onClick={() => toggleDetails(u.id)}
                  >
                    {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    {expanded ? t("adminUsers.hideDetails") : t("adminUsers.showDetails")}
                  </button>
                </div>
              </div>
              {expanded && <UserIdentityPanel entry={identities[u.id]} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
